import React, { useEffect, useRef, useState } from "react";
import { Check, Loader2, Mic, MoreVertical, Pencil, Square, Trash2, Type, Upload, Volume2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  useDeleteAudioClipMutation,
  useGenerateAudioClipMutation,
  useGetAudioClipsQuery,
  useRenameAudioClipMutation,
  useUploadAudioClipMutation,
} from "@/store/rest_api";
import { AudioClip } from "@/model/interface/FileResponse";
import { AudioLibrary } from "@/enum/AudioLibrary";
import { WavRecorder } from "@/util/wavRecorder";
import { apiErrorMessage } from "@/util/apiError";
import { audioDurationOf, formatClipSeconds } from "@/util/audioDuration";
import { DEFAULT_VOICE, VOICE_OPTIONS } from "@/util/ttsVoices";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const LIBRARY_COPY: Record<AudioLibrary, { empty: string; emptyHint: string; recordTitle: string; recordHint: string; namePlaceholder: string }> = {
  [AudioLibrary.AUDIO_CLIP]: {
    empty: "No clips yet",
    emptyHint: "Record hold music or a greeting for your call flows, or upload an MP3 or WAV.",
    recordTitle: "Record a clip",
    recordHint: "Keep it short and clear.",
    namePlaceholder: "Answer greeting",
  },
  [AudioLibrary.VOICEMAIL_MESSAGE]: {
    empty: "No voicemail messages yet",
    emptyHint: "Record the message agents drop on voicemail boxes, upload one, or have it read from text.",
    recordTitle: "Record a voicemail message",
    recordHint: "It plays when an agent drops it on a voicemail box. Say who you are and how to call back.",
    namePlaceholder: "Cash offer follow up",
  },
};

interface AudioClipLibraryProps {
  onSelect?: (clip: AudioClip) => void;
  category?: AudioLibrary;
}

export default function AudioClipLibrary({ onSelect, category = AudioLibrary.AUDIO_CLIP }: AudioClipLibraryProps) {
  const { data: clips = [], isLoading } = useGetAudioClipsQuery(category);
  const [uploadClip, uploadResult] = useUploadAudioClipMutation();
  const [generateClip, generateResult] = useGenerateAudioClipMutation();
  const [renameClip] = useRenameAudioClipMutation();
  const [deleteClip] = useDeleteAudioClipMutation();

  const [isRecordDialogOpen, setIsRecordDialogOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [clipName, setClipName] = useState("");
  const [renameTarget, setRenameTarget] = useState<AudioClip | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
  const [generateName, setGenerateName] = useState("");
  const [generateVoice, setGenerateVoice] = useState(DEFAULT_VOICE);
  const [generateText, setGenerateText] = useState("");

  const recorderRef = useRef<WavRecorder | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const copy = LIBRARY_COPY[category];

  const resetRecordingState = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setIsRecording(false);
    setRecordingSeconds(0);
    setRecordedBlob(null);
    setRecordedUrl(null);
    setClipName("");
  };

  const handleOpenRecordDialog = () => {
    resetRecordingState();
    setIsRecordDialogOpen(true);
  };

  const handleOpenGenerateDialog = () => {
    setGenerateName("");
    setGenerateVoice(DEFAULT_VOICE);
    setGenerateText("");
    setIsGenerateDialogOpen(true);
  };

  const handleStartRecording = async () => {
    try {
      recorderRef.current = new WavRecorder();
      await recorderRef.current.start();
      setIsRecording(true);
      setRecordingSeconds(0);
      timerRef.current = setInterval(() => setRecordingSeconds((seconds) => seconds + 1), 1000);
    } catch {
      toast.error("Microphone access denied");
    }
  };

  const handleStopRecording = () => {
    if (!recorderRef.current) {
      return;
    }
    const blob = recorderRef.current.stop();
    recorderRef.current = null;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsRecording(false);
    setRecordedBlob(blob);
    setRecordedUrl(URL.createObjectURL(blob));
  };

  const handleSaveRecording = async () => {
    if (!recordedBlob || !clipName.trim()) {
      return;
    }
    try {
      const saved = await uploadClip({
        file: recordedBlob,
        fileName: "recording.wav",
        name: clipName.trim(),
        category,
        durationSeconds: (await audioDurationOf(recordedBlob)) ?? recordingSeconds,
      }).unwrap();
      toast.success("Clip saved");
      setIsRecordDialogOpen(false);
      resetRecordingState();
      onSelect?.(saved);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to save the clip"));
    }
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    const defaultName = file.name.replace(/\.(mp3|wav)$/i, "");
    try {
      const saved = await uploadClip({
        file,
        fileName: file.name,
        name: defaultName,
        category,
        durationSeconds: await audioDurationOf(file),
      }).unwrap();
      toast.success("Clip uploaded");
      onSelect?.(saved);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to upload the clip"));
    }
  };

  const handleGenerate = async () => {
    if (!generateName.trim() || !generateText.trim()) {
      return;
    }
    try {
      const saved = await generateClip({
        name: generateName.trim(),
        voice: generateVoice,
        text: generateText.trim(),
        category,
      }).unwrap();
      toast.success("Message created");
      setIsGenerateDialogOpen(false);
      onSelect?.(saved);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not create the message"));
    }
  };

  const handleRename = async () => {
    if (!renameTarget || !renameValue.trim()) {
      return;
    }
    try {
      await renameClip({ id: renameTarget.id, name: renameValue.trim() }).unwrap();
      setRenameTarget(null);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to rename the clip"));
    }
  };

  const handleDelete = async (clip: AudioClip) => {
    try {
      await deleteClip(clip.id).unwrap();
      toast.success("Clip deleted");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to delete the clip"));
    }
  };

  useEffect(() => {
    return () => {
      if (recordedUrl) {
        URL.revokeObjectURL(recordedUrl);
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [recordedUrl]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
          <Upload className="mr-2 h-4 w-4" />
          Upload
        </Button>
        <Button type="button" variant="outline" onClick={handleOpenGenerateDialog}>
          <Type className="mr-2 h-4 w-4" />
          From text
        </Button>
        <Button type="button" onClick={handleOpenRecordDialog}>
          <Mic className="mr-2 h-4 w-4" />
          Record
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".mp3,.wav,audio/mpeg,audio/wav"
          className="hidden"
          onChange={handleFileSelected}
        />
      </div>

      {clips.length === 0 ? (
        <div className="flex flex-col items-center py-10 text-center">
          <Volume2 className="h-10 w-10 text-muted-foreground opacity-40 mb-3" />
          <p className="text-sm text-muted-foreground">{copy.empty}</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-sm">{copy.emptyHint}</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {clips.map((clip) => (
            <div key={clip.id} className="flex items-center gap-3 rounded-lg border p-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate dark:text-white">{clip.name}</p>
                <p className="text-xs text-muted-foreground uppercase">
                  {clip.extension}
                  {clip.durationSeconds ? ` · ${formatClipSeconds(clip.durationSeconds)}` : ""}
                </p>
              </div>
              <audio controls preload="none" src={clip.url} className="h-9 max-w-60" />
              {onSelect && (
                <Button type="button" size="sm" onClick={() => onSelect(clip)}>
                  <Check className="h-3 w-3 mr-1" />
                  Use
                </Button>
              )}
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => {
                  setRenameTarget(clip);
                  setRenameValue(clip.name);
                }}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" size="icon" variant="ghost" title="Options">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(clip)}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete clip
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>
      )}

      <Dialog
        open={isRecordDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            if (isRecording && recorderRef.current) {
              recorderRef.current.stop();
              recorderRef.current = null;
            }
            resetRecordingState();
          }
          setIsRecordDialogOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{copy.recordTitle}</DialogTitle>
            <DialogDescription>{copy.recordHint}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex flex-col items-center gap-3 py-4">
              {!isRecording && !recordedBlob && (
                <Button type="button" size="lg" className="rounded-full h-16 w-16" onClick={handleStartRecording}>
                  <Mic className="h-6 w-6" />
                </Button>
              )}
              {isRecording && (
                <>
                  <Button
                    type="button"
                    size="lg"
                    variant="destructive"
                    className="rounded-full h-16 w-16"
                    onClick={handleStopRecording}
                  >
                    <Square className="h-6 w-6" />
                  </Button>
                  <span className="text-sm font-mono text-red-500">{formatClipSeconds(recordingSeconds)}</span>
                </>
              )}
              {recordedUrl && !isRecording && (
                <div className="w-full space-y-3">
                  <audio controls src={recordedUrl} className="w-full" />
                  <Button type="button" variant="outline" size="sm" onClick={resetRecordingState}>
                    Record again
                  </Button>
                </div>
              )}
            </div>

            {recordedBlob && (
              <div className="space-y-1.5">
                <Label htmlFor="clip-name">Clip name</Label>
                <Input
                  id="clip-name"
                  value={clipName}
                  onChange={(event) => setClipName(event.target.value)}
                  placeholder={copy.namePlaceholder}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              onClick={handleSaveRecording}
              disabled={!recordedBlob || !clipName.trim() || uploadResult.isLoading}
            >
              {uploadResult.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save clip
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isGenerateDialogOpen} onOpenChange={setIsGenerateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create from text</DialogTitle>
            <DialogDescription>A voice reads your text and it is saved as a clip.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="generate-name">Name</Label>
              <Input
                id="generate-name"
                value={generateName}
                onChange={(event) => setGenerateName(event.target.value)}
                placeholder={copy.namePlaceholder}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Voice</Label>
              <Select value={generateVoice} onValueChange={setGenerateVoice}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VOICE_OPTIONS.map((voice) => (
                    <SelectItem key={voice.value} value={voice.value}>{voice.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="generate-text">Text</Label>
              <Textarea
                id="generate-text"
                value={generateText}
                onChange={(event) => setGenerateText(event.target.value)}
                rows={5}
                maxLength={1000}
                placeholder="Hi, this is Sam with Oak Street Homes. I'm calling about your property. Call me back at 555 0100."
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setIsGenerateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleGenerate()}
              disabled={!generateName.trim() || !generateText.trim() || generateResult.isLoading}
            >
              {generateResult.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={renameTarget != null} onOpenChange={(open) => !open && setRenameTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename clip</DialogTitle>
          </DialogHeader>
          <Input value={renameValue} onChange={(event) => setRenameValue(event.target.value)} />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setRenameTarget(null)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleRename} disabled={!renameValue.trim()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
